import Delete from "@mui/icons-material/Delete";
import { type GridRenderCellParams, type GridRowSelectionModel } from "@mui/x-data-grid-pro";
import axios, { isAxiosError } from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import apiRoutes from "../../../constants/api-routes";
import messageLevels from "../../../constants/message-levels";
import messages from "../../../constants/messages";
import navigationItemReferences from "../../../constants/navigation-item-references";
import {
	emptyGridRowSelectionModel, gridRowSelectionIdsArray,
	gridRowSelectionSize, resolveGridSelectedRowIds
} from "../../../helpers/dataGridRowSelectionModel";
import useAuthHeader from "../../../hooks/useAuthHeader";
import useCoAppNavigation from "../../../hooks/useCoAppNavigation";
import useDatagridSorting from "../../../hooks/useDatagridSorting";
import useToastAlert from "../../../hooks/useToastAlert";
import { selectOrganizationIdentityProvider, selectOrganizationSsoAndScim } from "../../../redux/userSlice";
import CoAppDatagridHeader from "../../global/components/datagrid/CoAppDatagridHeader";
import CoAppStandardDataGrid from "../../global/components/datagrid/CoAppStandardDataGrid";
import useCoAppDataGridFilters from "../../global/components/datagrid/useCoAppDataGridFilters";
import CoAppCreateGroupOrRoleModal from "../../global/components/modals/CoAppCreateGroupOrRoleModal";
import CoAppDestructiveConfirmationModal from "../../global/components/modals/CoAppDestructiveConfirmationModal";
import SettingsDataGridSelectionRow, { destructiveIconSx } from "../SettingsDataGridSelectionRow";
import { getRoleManagementColumns, type RoleRow } from "./columns/role-management-columns";

function openRoleEditPageAdapter(
	openResourceEditPage: ReturnType<typeof useCoAppNavigation>["openResourceEditPage"],
) {
	return (params: GridRenderCellParams<RoleRow>, openedFrom: number, parentName: string) => {
		const { row } = params;
		openResourceEditPage(
			{
				id: params.id == null ? undefined : String(params.id),
				row: {
					name: row.name,
					isSuperAdmin: typeof row.isSuperAdmin === "boolean" ? row.isSuperAdmin : undefined,
					active: typeof row.active === "boolean" ? row.active : undefined,
					id: row.id == null ? undefined : String(row.id),
				},
			},
			openedFrom,
			parentName,
		);
	};
}

export default function RoleManagement() {
	const organizationSsoAndScim = useSelector(selectOrganizationSsoAndScim);
	const authHeader = useAuthHeader();
	const { openResourceEditPage } = useCoAppNavigation();
	const openRoleEditPage = openRoleEditPageAdapter(openResourceEditPage);
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("roles");
	const { handleToastAlert } = useToastAlert();
	const identityProviderName = useSelector(selectOrganizationIdentityProvider);

	const [newRoleName, setNewRoleName] = useState<string>("");
	const [roleCreationDialogOpen, setRoleCreationDialogOpen] = useState<boolean>(false);
	const [roleDeleteDialogOpen, setRoleDeleteDialogOpen] = useState<boolean>(false);
	const [roleSeed, setRoleSeed] = useState<number>(0);
	const [roleBeingDeleted, setRoleBeingDeleted] = useState<RoleRow | null>(null);
	const [roleNameError, setRoleNameError] = useState<string>("");
	const [roles, setRoles] = useState<RoleRow[]>([]);
	const [selectedRoles, setSelectedRoles] = useState<GridRowSelectionModel>(() => emptyGridRowSelectionModel());
	const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(true);

	const handleOpenRoleDeletionDialog = useCallback((role: RoleRow) => {
		setRoleBeingDeleted(role);
		setRoleDeleteDialogOpen(true);
	}, []);

	const duplicateRole = useCallback((id: string | number) => {
		axios.post(apiRoutes.copyRole(id), {}, { headers: authHeader })
			.then(() => {
				setRoleSeed(Math.random());
			})
			.catch((err: unknown) => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.ROLE_DUPLICATION_ERROR_MSG);
			});
	}, [authHeader, handleToastAlert]);

	// Stable column defs — new arrays on each render make MUI X discard autosized widths.
	const columns = useMemo(
		() => getRoleManagementColumns(
			openRoleEditPage,
			duplicateRole,
			handleOpenRoleDeletionDialog,
			organizationSsoAndScim ?? false,
			identityProviderName,
			navigationItemReferences,
		),
		[organizationSsoAndScim, identityProviderName]
	);

	const handleRoleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setNewRoleName(e.target.value);
		setRoleNameError("");
	};

	const createNewRole = () => {
		if (!newRoleName) {
			setRoleNameError("Required *");
			return;
		}

		const role = { name: newRoleName };

		axios.post(apiRoutes.createRole, role, { headers: authHeader })
			.then(() => {
				initRoleManagement();
				toggleAddNewDialog();
			})
			.catch((err: unknown) => {
				console.log(err);
				if (!isAxiosError(err)) {
					toggleAddNewDialog();
					handleToastAlert(messageLevels.ERROR, messages.ROLE_CREATION_ERROR_MSG);
					return;
				}
				const data = err.response?.data;
				if (data && typeof data === "object" && "message" in data
					&& typeof (data as { message?: string }).message === "string"
					&& (data as { message: string }).message.includes("already exists")) {
					setRoleNameError(messages.RESOURCE_ALREADY_EXISTS_ERROR_MSG("Role"));
					return;
				}
				toggleAddNewDialog();
				handleToastAlert(messageLevels.ERROR, messages.ROLE_CREATION_ERROR_MSG);
			});
	};

	const deleteRole = () => {
		const id = roleBeingDeleted?.id;
		if (id == null) return;
		axios.delete(apiRoutes.deleteRole(id), { headers: authHeader })
			.then(() => {
				setRoleSeed(Math.random());
				handleCloseRoleDeletionDialog();
			})
			.catch((err) => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.ROLE_DELETION_ERROR_MSG);
				handleCloseRoleDeletionDialog();
			});
	};

	const bulkDeleteRoles = () => {
		const roleIds = gridRowSelectionIdsArray(selectedRoles, roles);
		const deleteData = { roleIds };
		const plural = roleIds.length > 1;

		axios.delete(apiRoutes.bulkDeleteRoles, { headers: authHeader, data: deleteData })
			.then(() => {
				setRoleSeed(Math.random());
				setBulkDeleteDialogOpen(false);
				setSelectedRoles(emptyGridRowSelectionModel());
			})
			.catch((err) => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.ROLE_DELETION_ERROR_MSG);
				setBulkDeleteDialogOpen(false);
			});
	};

	const handleCloseRoleDeletionDialog = () => {
		setRoleBeingDeleted(null);
		setRoleDeleteDialogOpen(false);
	};

	const sortRolesPermissions = (roleList: RoleRow[]) => {
		return roleList.map((role) => {
			const permissions = role.permissions;
			return {
				...role,
				permissions: permissions
					? [...permissions].sort((a, b) =>
						(a.friendlyName ?? "").localeCompare(b.friendlyName ?? "")
					)
					: undefined,
			};
		});
	};

	const initRoleManagement = () => {
		axios.get(apiRoutes.getRoles, { headers: authHeader })
			.then((res) => {
				const rolesWithSortedPermissions = sortRolesPermissions(res.data as RoleRow[]);
				setRoles(rolesWithSortedPermissions);
				setLoading(false);
			})
			.catch((err) => {
				console.log(err);
			});
	};

	const toggleAddNewDialog = () => {
		setRoleCreationDialogOpen(!roleCreationDialogOpen);
		setNewRoleName("");
		setRoleNameError("");
	};

	useEffect(() => {
		initRoleManagement();
		handleInitializeSortModel();
	}, [roleSeed]);

	const { gridFilterModel, onFilterModelChange, quickFilterSearchProps, apiRef, visibleRowCount, filterControl, filterButtonEl } = useCoAppDataGridFilters(roles, loading);
	const selectedRoleCount = resolveGridSelectedRowIds(selectedRoles, roles).length;

	return (
		<>
			<CoAppDatagridHeader
				iconKind="roles"
				searchIsPresent={true}
				{...quickFilterSearchProps}
				searchPlaceholder="Search all columns…"
				filterControl={filterControl}
				title="Roles"
				resourceType="Role"
				addIsPresent={true}
				addOnClickHandler={toggleAddNewDialog}
				editIsPresent={false}
				deleteIsPresent={false}
			/>
			<CoAppStandardDataGrid
				apiRef={apiRef}
				filterButtonEl={filterButtonEl}
				filterModel={gridFilterModel}
				onFilterModelChange={onFilterModelChange}
				visibleRowCount={visibleRowCount}
				columns={columns}
				rows={roles}
				pinnedColumns={["actions"]}
				targetResource="roles"
				gridKey="roles"
				selectionModel={selectedRoles}
				setSelectionModel={setSelectedRoles}
				loading={loading}
				sortModel={sortModel}
				handleSortModelChange={handleSortModelChange}
				selectionFooter={
					<SettingsDataGridSelectionRow
						resourceNoun="role"
						visibleCount={visibleRowCount}
						selectedCount={selectedRoleCount}
						loading={loading}
						onClearSelection={() => setSelectedRoles(emptyGridRowSelectionModel())}
						actions={[{
							label: "Delete",
							icon: <Delete sx={destructiveIconSx} />,
							onClick: () => setBulkDeleteDialogOpen(true),
							variant: "destructive",
						}]}
					/>
				}
			/>
			<CoAppCreateGroupOrRoleModal
				cancelClickHandler={toggleAddNewDialog}
				changeHandler={handleRoleNameChange}
				dialogOpen={roleCreationDialogOpen}
				nameError={roleNameError}
				confirmClickHandler={createNewRole}
				isRole={true}
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={roleDeleteDialogOpen}
				dialogTitle={`Delete ${roleBeingDeleted?.name} role?`}
				dialogMessage={messages.DELETION_CONFIRMATION_MSG(roleBeingDeleted?.name ?? "", "role")}
				confirmClickHandler={deleteRole}
				cancelClickHandler={handleCloseRoleDeletionDialog}
				actionText="Delete"
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={bulkDeleteDialogOpen}
				dialogTitle={`Delete selected ${gridRowSelectionSize(selectedRoles, roles) > 1 ? "roles" : "role"}?`}
				dialogMessage={"Are you sure you want to bulk delete all the selected roles? This action cannot be undone."}
				confirmClickHandler={bulkDeleteRoles}
				cancelClickHandler={() => setBulkDeleteDialogOpen(false)}
				actionText="Delete"
			/>
		</>
	);
}
